import { useMemo, useState } from 'react';
import { IconArrowLeft, IconBell, IconChevronLeft, IconChevronRight, IconCopy, IconDots, IconLock, IconPlus, IconSearch, IconSend } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Chip, Modal, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { meeCalendar } from '../data';
import {
  BRANCHES, BRANCH_NAME, CODE, CONFIRMED_DEFAULT, DAYS, DOW, EDIT_CODES, METHODS, POINTS, POINT_METHODS, ROTA, RULES_DEFAULT, SHIFT, SHIFT_CYCLE,
  STAFF, TODAY, WEEK_DATES, WEEK_DAYS, clamp, codeOf, countEmpty, coverage, dowOf, faceMissing, filterStaff, fmt1, kindOf, methodDetail, punchOf, qrCells,
  rotateFill, shiftOf, totals, type Branch, type Code, type Point, type Rules, type ShiftCode, type Method, type Staff,
} from './attend';
import './attend.i18n';
import s from './attend.module.css';

// Mint-HR-Attendance (design-specs/Mint-HR-Attendance.md): 4 tab · Bảng công · Phân ca · Điểm chấm · Quy tắc công.
// Giờ / ngày làm việc / T7 nửa ngày / lễ CHỈ qua @hana/work-calendar. Chỉ đọc + thao tác giả lập trong bộ nhớ (Hoàn tác). Không ghi DB.

type Tab = 'sheet' | 'shift' | 'pts' | 'rule';
const TONE: Record<string, string> = { ok: s.tOk ?? '', warn: s.tWarn ?? '', err: s.tErr ?? '', pri: s.tPri ?? '', mute: s.tMute ?? '' };
const CODE_KEY: Record<Code, string> = { ok: 'hra.cellOk', M: 'hra.cellM', '!': 'hra.cellMiss', P: 'hra.cellP', CT: 'hra.cellCT', off: 'hra.cellOff', fut: 'hra.cellFut' };
const ED_KEY: Record<string, string> = { ok: 'hra.edOk', M: 'hra.edM', '!': 'hra.edMiss', P: 'hra.edP', CT: 'hra.edCT' };

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function Pill({ on, onClick, children, big }: { on: boolean; onClick: () => void; children: string; big?: boolean }) {
  return <button type="button" aria-pressed={on} className={`${s.pill} ${big ? s.pillBig : ''} ${on ? s.pillOn : ''}`} onClick={onClick}>{children}</button>;
}
function Switch({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} className={`${s.sw} ${on ? s.swOn : ''}`} onClick={onClick}><span aria-hidden="true" /></button>;
}

export function HrAttendance() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const cal = useMemo(() => meeCalendar(), []);
  const forced = readState();
  const [tab, setTab] = useState<Tab>('sheet');
  const [dense, setDense] = useState<'std' | 'compact'>('std');
  const [more, setMore] = useState(false);
  const [br, setBr] = useState<Branch | 'all'>('all');
  const [q, setQ] = useState('');
  const [sheetOv, setSheetOv] = useState<Record<string, Code>>({});
  const [conf, setConf] = useState<Record<string, boolean>>({});
  const [cell, setCell] = useState<{ u: string; d: number } | null>(null);
  const [ed, setEd] = useState<{ c: Code; why: string; tried: boolean } | null>(null);
  const [locked, setLocked] = useState(false);
  const [confirmLock, setConfirmLock] = useState(false);
  const [wk, setWk] = useState(45);
  const [shOv, setShOv] = useState<Record<string, ShiftCode>>({});
  const [ptOv, setPtOv] = useState<Record<string, Partial<Record<Method, boolean>>>>({});
  const [ptAdd, setPtAdd] = useState<Point[]>([]);
  const [qrOf, setQrOf] = useState<string | null>(null);
  const [pf, setPf] = useState<{ name: string; addr: string; r: string; m: Partial<Record<Method, boolean>>; tried: boolean } | null>(null);
  const [ru, setRu] = useState<Rules>(RULES_DEFAULT);
  const [ncr, setNcr] = useState<string[]>(['PHILIP', 'DAVID']);

  const say = (msg: string, undo?: () => void) => toast({ msg, ...(undo ? { undo } : {}) });
  const staff = filterStaff(br, q);
  const rota = ROTA.filter((r) => br === 'all' || r[2] === br);
  const emptyN = countEmpty(rota, wk, shOv);
  const confirmed = (u: string) => !!conf[u] || CONFIRMED_DEFAULT.includes(u);
  const xnN = staff.filter((p) => confirmed(p.user)).length;
  const state: ScreenState = forced ?? 'ready';
  const newPoint = () => setPf({ name: '', addr: '', r: '100', m: { gps: true }, tried: false });

  const primary =
    tab === 'sheet' ? (locked ? { k: t('hra.pLocked'), act: () => say(t('hra.tLocked')) } : { k: t('hra.pLock'), act: () => setConfirmLock(true) })
      : tab === 'shift' ? { k: t('hra.pRota'), act: () => { const prev = shOv; setShOv(rotateFill(wk, shOv)); say(t('hra.tRota', { n: emptyN }), () => setShOv(prev)); } }
        : tab === 'pts' ? { k: t('hra.pPt'), act: newPoint }
          : { k: t('hra.pSave'), act: () => say(t('hra.tRuleSaved')) };

  const kpis: [string, string, string, string?][] = [
    [t('hra.kStd'), fmt1(cal.standardDaysInMonth('2026-10')), t('hra.kStdU')],
    [t('hra.kNow'), '31 / 36', t('hra.kNowU')],
    [t('hra.kWait'), '5', t('hra.kWaitU'), 'warn'],
    [t('hra.kShift'), String(emptyN), t('hra.kShiftU', { w: wk }), emptyN ? 'warn' : 'ok'],
  ];
  const menu: [string, string, () => void][] = [
    [t('hra.mExp'), t('hra.mExpSub', { n: staff.length }), () => say(t('hra.tExp', { n: staff.length }))],
    [t('hra.mLoad'), t('hra.mLoadSub'), () => say(t('hra.tLoad'))],
    [t('hra.mHr'), t('hra.mHrSub'), () => void navigate({ to: '/mee/hr' })],
  ];
  const msgState = state === 'empty'
    ? { title: t('hra.emptyTitle'), desc: t('hra.emptyDesc'), action: <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={() => { setTab('pts'); newPoint(); }}>{t('hra.pPt')}</Button> }
    : state === 'error'
      ? { title: t('hra.errTitle'), desc: t('hra.errDesc'), action: <Button onClick={() => say(t('hra.retrying'))}>{t('hra.retry')}</Button> }
      : { title: t('hra.npTitle'), desc: t('hra.npDesc'), action: <Button onClick={() => void navigate({ to: '/mee' })}>{t('hra.npBtn')}</Button> };

  const cs: Staff | undefined = cell ? STAFF.find((p) => p.user === cell.u) : undefined;
  const cc: Code = cs && cell ? codeOf(cal, cs, cell.d, sheetOv) : 'ok';
  const cellAria = (p: Staff, d: number, c: Code) => `${p.user} · ${DOW[dowOf(d)]} ${d}/Oct · ${t(CODE_KEY[c])}${kindOf(cal, d) === 'half' ? t('hra.half') : ''}`;
  const saveCell = () => {
    if (!cs || !cell || !ed) return;
    if (!ed.why.trim()) { setEd({ ...ed, tried: true }); return; }
    const prev = sheetOv;
    setSheetOv({ ...sheetOv, [`${cs.user}:${cell.d}`]: ed.c });
    say(t('hra.tFixed', { u: cs.user, d: cell.d }), () => setSheetOv(prev));
    setCell(null); setEd(null);
  };
  const closeCell = () => { setCell(null); setEd(null); };
  const pts = [...POINTS, ...ptAdd].filter((p) => br === 'all' || p.code === br || !(p.code in BRANCH_NAME));
  const submitPf = () => {
    if (!pf) return;
    if (!pf.name.trim()) { setPf({ ...pf, tried: true }); return; }
    const prev = ptAdd;
    setPtAdd([...ptAdd, { code: 'NEW', name: pf.name, addr: pf.addr || '—', radius: Number(pf.r) || 100, on: { gps: false, wifi: false, qr: false, face: false, ...pf.m } as Record<Method, boolean>, wifi: [], faces: '—' }]);
    say(t('hra.tPtNew', { n: pf.name }), () => setPtAdd(prev));
    setPf(null);
  };
  const ruleStep = (k: 'late' | 'early' | 'fix', d: number, hi: number) => () => setRu({ ...ru, [k]: clamp(ru[k] + d, 0, hi) });
  const legend: Code[] = ['ok', 'M', '!', 'P', 'CT'];
  const legendKey: Record<string, string> = { ok: 'hra.lgOk', M: 'hra.lgM', '!': 'hra.lgMiss', P: 'hra.lgP', CT: 'hra.lgCT' };
  const dates = WEEK_DATES[wk] ?? WEEK_DATES[45]!;

  return (
    <Page kind="table">
      <PageHeader
        title={t('hra.title')}
        meta={locked ? <Chip tone="ok">{t('hra.locked')}</Chip> : <span className={s.mute}>{t('hra.sub')}</span>}
        lead={<HeaderTool label={t('hra.back')} icon={IconArrowLeft} onClick={() => void navigate({ to: '/mee/hr' })} />}
        actions={
          <>
            <Segmented kind="toggle" label={t('hra.density')} value={dense} onChange={setDense} items={[{ key: 'std', label: t('hra.dStd') }, { key: 'compact', label: t('hra.dCompact') }]} />
            <HeaderTool label={t('hra.notif')} icon={IconBell} onClick={() => say(t('hra.tNotif'))} />
            <HeaderTool label={t('hra.more')} icon={IconDots} active={more} onClick={() => setMore((v) => !v)} />
            {state === 'ready' && <Button variant="primary" icon={tab === 'pts' ? <IconPlus size={16} aria-hidden="true" /> : tab === 'sheet' ? <IconLock size={16} aria-hidden="true" /> : undefined} onClick={primary.act}>{primary.k}</Button>}
          </>
        }
      />
      {more && (
        <div role="menu" aria-label={t('hra.more')} className={s.menu}>
          {menu.map(([k, sub, fn]) => <button key={k} type="button" role="menuitem" onClick={() => { setMore(false); fn(); }}>{k}<span>{sub}</span></button>)}
        </div>
      )}
      <div className={`${s.page} ${dense === 'compact' ? s.dense : ''}`}>
        <div className={s.tabs}>
          <Segmented label={t('hra.title')} value={tab} onChange={(k) => { setTab(k); setCell(null); }} items={[
            { key: 'sheet', label: t('hra.tSheet') }, { key: 'shift', label: t('hra.tShift'), ...(emptyN ? { n: emptyN } : {}) },
            { key: 'pts', label: t('hra.tPts'), n: 3 }, { key: 'rule', label: t('hra.tRule') },
          ]} />
        </div>
        <StateView state={state} title={msgState.title} desc={msgState.desc} action={msgState.action} skeleton={<Skeleton rows={9} />}>
          <div className={s.kpis}>
            {kpis.map(([k, v, u, tn]) => (
              <div key={k} className={s.kpi}>
                <div className={s.kpiK}>{k}</div>
                <div className={s.kpiV}><b className={tn === 'warn' ? s.warn : tn === 'ok' ? s.okc : undefined}>{v}</b><span>{u}</span></div>
              </div>
            ))}
          </div>

          <div className={s.bar}>
            {tab === 'sheet' && (
              <label className={s.search}>
                <IconSearch size={16} stroke={2.2} aria-hidden="true" />
                <input aria-label={t('hra.search')} placeholder={t('hra.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
            )}
            {tab !== 'rule' && (
              <div role="group" aria-label={t('hra.brAria')} className={s.filters}>
                {BRANCHES.map(([k, lb]) => <Pill key={k} big on={br === k} onClick={() => { setBr(k); setCell(null); }}>{lb}</Pill>)}
              </div>
            )}
            {tab === 'shift' && (
              <div className={s.wk}>
                <button type="button" className={s.sq} aria-label={t('hra.wkPrev')} disabled={wk === 45} onClick={() => setWk(45)}><IconChevronLeft size={16} aria-hidden="true" /></button>
                <b>{t('hra.wkLabel', { w: wk, a: dates[0]!, b: dates[6]! })}</b>
                <button type="button" className={s.sq} aria-label={t('hra.wkNext')} disabled={wk === 46} onClick={() => setWk(46)}><IconChevronRight size={16} aria-hidden="true" /></button>
              </div>
            )}
            {tab === 'sheet' && (
              <>
                <span className={`${s.mute} ${s.mlAuto}`}>{t('hra.xnCount', { n: xnN, m: STAFF.length })}</span>
                <Button icon={<IconSend size={16} aria-hidden="true" />} onClick={() => { const prev = conf; setConf(Object.fromEntries(STAFF.map((p) => [p.user, true]))); say(t('hra.tSendXn'), () => setConf(prev)); }}>{t('hra.xnSend')}</Button>
              </>
            )}
          </div>

          {tab === 'sheet' && (
            <section className={s.card} aria-label={t('hra.sheetAria')}>
              <div className={s.sheetHd}>
                <h2>{t('hra.sheetHd')}</h2>
                <span className={s.mute} title={t('hra.sheetTip')}>{t('hra.sheetNote')}</span>
                <div className={s.legend}>
                  {legend.map((c) => <span key={c}><i className={`${s.lgv} ${TONE[CODE[c].tone]}`}>{CODE[c].v}</i>{t(legendKey[c]!)}</span>)}
                </div>
              </div>
              <div role="grid" aria-label={t('hra.sheetGrid')} className={s.sheet}>
                <div role="columnheader" className={`${s.sh} ${s.shName}`}>{t('hra.cStaff')}</div>
                {DAYS.map((d) => {
                  const off = kindOf(cal, d) === 'off';
                  return <div key={d} role="columnheader" className={`${s.sh} ${s.shDay} ${off ? s.shOff : ''} ${d === TODAY ? s.shToday : ''}`}><span>{DOW[dowOf(d)]}</span><span>{d}</span></div>;
                })}
                {([['hra.cCong', 'hra.cCongTip'], ['hra.cLate', 'hra.cLateTip'], ['hra.cMiss', 'hra.cMissTip'], ['hra.cLeave', 'hra.cLeaveTip'], ['hra.cXn', 'hra.cXnTip']] as const).map(([k, tip]) => (
                  <div key={k} role="columnheader" title={t(tip)} className={`${s.sh} ${s.shNum}`}>{t(k)}</div>
                ))}
                {staff.map((p) => {
                  const tt = totals(cal, p, sheetOv);
                  const ok = confirmed(p.user);
                  return (
                    <SheetRow key={p.user}>
                      <div role="rowheader" className={s.rowH}><b>{p.user}</b><span>{p.seat} · {BRANCH_NAME[p.branch]}</span></div>
                      {DAYS.map((d) => {
                        const c = codeOf(cal, p, d, sheetOv);
                        const off = kindOf(cal, d) === 'off';
                        const sel = !!cell && cell.u === p.user && cell.d === d;
                        const a = cellAria(p, d, c);
                        return (
                          <button key={d} type="button" role="gridcell" disabled={off || c === 'fut'} aria-label={a} title={a}
                            className={`${s.gc} ${off ? s.gcOff : c === 'ok' || c === 'fut' ? '' : TONE[CODE[c].tone]} ${c === 'ok' ? s.gcOk : ''} ${sel ? s.gcSel : ''}`}
                            onClick={() => { setCell({ u: p.user, d }); setEd(null); }}>{CODE[c].v}</button>
                        );
                      })}
                      <div className={`${s.num} ${s.bold}`}>{fmt1(tt.cong)}</div>
                      <div className={`${s.num} ${tt.late ? s.warn : ''}`}>{tt.late}</div>
                      <div className={`${s.num} ${tt.miss ? s.errc : ''}`}>{tt.miss}</div>
                      <div className={s.num}>{fmt1(tt.leave)}</div>
                      <div className={s.num}><span className={`${s.chip} ${TONE[ok ? 'ok' : 'warn']}`}>{ok ? t('hra.xnDone') : t('hra.xnWait')}</span></div>
                    </SheetRow>
                  );
                })}
              </div>
              {staff.length === 0 && <div className={s.noMatch}>{t('hra.noStaff')}</div>}
            </section>
          )}

          {tab === 'shift' && (
            <div className={s.shGrid}>
              <section className={s.card} aria-label={t('hra.shiftAria')}>
                <div className={s.sheetHd}>
                  <h2>{t('hra.shiftHd')}</h2>
                  <span className={`${s.chipL} ${TONE[emptyN ? 'warn' : 'ok']}`}>{emptyN ? t('hra.shiftEmpty', { n: emptyN }) : t('hra.shiftFull')}</span>
                  <div className={`${s.row} ${s.mlAuto}`}>
                    <Button size="sm" icon={<IconCopy size={16} aria-hidden="true" />} onClick={() => say(t('hra.tCopy', { w: wk }))}>{t('hra.copyWk')}</Button>
                    <Button size="sm" icon={<IconSend size={16} aria-hidden="true" />} onClick={() => say(t('hra.tSendSh', { w: wk, n: rota.length }))}>{t('hra.sendSh')}</Button>
                  </div>
                </div>
                <div role="grid" aria-label={t('hra.shiftGrid')} className={s.shTbl}>
                  <div role="columnheader" className={s.th}>{t('hra.cStaff')}</div>
                  {WEEK_DAYS.map((w, i) => <div key={w} role="columnheader" className={s.thc}><b>{w}</b> <span className={s.mute}>{dates[i]}/11</span></div>)}
                  {rota.map((r) => (
                    <SheetRow key={r[0]}>
                      <div role="rowheader" className={s.shRow}><b>{r[0]}</b><span className={s.sub}>{BRANCH_NAME[r[2]]}</span></div>
                      {WEEK_DAYS.map((w, i) => {
                        const c = shiftOf(r, i, wk, shOv);
                        const empty = c === '_';
                        const S0 = empty ? null : SHIFT[c];
                        const name = empty ? t('hra.shUnset') : c === 'N' ? t('hra.shOff') : S0!.k;
                        const a = t('hra.shCellAria', { u: r[0], w, d: dates[i]!, k: empty ? t('hra.shUnsetAria') : S0!.k });
                        return (
                          <button key={w} type="button" role="gridcell" aria-label={a} title={a} className={`${s.shCell} ${empty ? s.shEmpty : c === 'N' ? s.tMute : TONE[S0!.tone]}`}
                            onClick={() => setShOv({ ...shOv, [`${wk}:${r[0]}:${i}`]: SHIFT_CYCLE[(SHIFT_CYCLE.indexOf(c) + 1) % SHIFT_CYCLE.length]! })}>
                            <span>{name}</span><span>{S0?.sub ?? ''}</span>
                          </button>
                        );
                      })}
                    </SheetRow>
                  ))}
                  <div className={s.coverK}>{t('hra.cover')}</div>
                  {coverage(wk, shOv).map((v, i) => <div key={WEEK_DAYS[i]} className={`${s.cover} ${v.short ? s.warn : ''}`} title={v.short ? t('hra.coverShort') : t('hra.coverOk')}>{`S ${v.s} · C ${v.c} · Đ ${v.d}`}</div>)}
                </div>
                {rota.length === 0 && <div className={s.noMatch}>{t('hra.noShift')}</div>}
              </section>
              <aside className={s.card} aria-label={t('hra.tplHd')}>
                <h2>{t('hra.tplHd')}</h2>
                {(['S', 'C', 'D', 'HC'] as const).map((k) => (
                  <div key={k} className={s.tpl}><span aria-hidden="true" className={`${s.tplBar} ${TONE[SHIFT[k].tone]}`} /><div><b>{SHIFT[k].k}</b><div className={s.sub}>{SHIFT[k].sub}</div></div></div>
                ))}
                <div className={s.sub}>{t('hra.tplHint')}</div>
              </aside>
            </div>
          )}

          {tab === 'pts' && (
            <div className={s.ptGrid}>
              {pts.map((p) => {
                const m = { ...p.on, ...(ptOv[p.name] ?? {}) };
                const n = METHODS.filter(([k]) => m[k]).length;
                const miss = faceMissing(p);
                return (
                  <section key={p.name} className={s.card} aria-label={p.name} style={{ gap: 4 }}>
                    <div className={s.row}><h2 className={s.ptName}>{p.name}</h2><span className={s.sub}>{t('hra.ptN', { n })}</span></div>
                    <div className={s.sub} style={{ marginBottom: 4 }}>{p.addr}</div>
                    {METHODS.map(([k, lb, tip]) => (
                      <div key={k} className={s.mrow}>
                        <span aria-hidden="true" className={`${s.mic} ${m[k] ? '' : s.mute}`}>{k === 'gps' ? 'GPS' : k === 'wifi' ? 'Wi' : k === 'qr' ? 'QR' : 'ID'}</span>
                        <div className={s.mtx} title={tip}><div className={`${s.krK} ${m[k] ? '' : s.mute}`}>{lb}</div><div className={s.sub}>{methodDetail(p, k)}</div></div>
                        {k === 'qr' && m[k] && <button type="button" className={s.act} onClick={() => setQrOf(p.name)}>{t('hra.qrShow')}</button>}
                        {k === 'face' && m[k] && miss !== null && <button type="button" className={s.act} onClick={() => say(t('hra.tFace', { n: miss }))}>{t('hra.faceRemind')}</button>}
                        <Switch on={!!m[k]} label={lb} onClick={() => { const prev = ptOv; setPtOv({ ...ptOv, [p.name]: { ...(ptOv[p.name] ?? {}), [k]: !m[k] } }); say(t('hra.tToggle', { a: m[k] ? t('hra.tOff') : t('hra.tOn'), k: lb, p: p.name }), () => setPtOv(prev)); }} />
                      </div>
                    ))}
                  </section>
                );
              })}
              <button type="button" className={s.newPt} onClick={newPoint}><IconPlus size={16} aria-hidden="true" />{t('hra.ptNew')}</button>
            </div>
          )}

          {tab === 'rule' && (
            <div className={s.ruGrid}>
              <section className={`${s.card} ${s.ruCard}`} aria-label={t('hra.ruleAria')}>
                <div className={s.rule}><div className={s.rk}><div className={s.krK}>{t('hra.r1')}</div><div className={s.sub}>{t('hra.r1s')}</div></div><button type="button" className={s.act} onClick={() => say(t('hra.tAdmin'))}>{t('hra.r1v')} ↗</button></div>
                {([['late', 'hra.r2', 'hra.r2s', 30, 'hra.minutes'], ['early', 'hra.r3', 'hra.r3s', 30, 'hra.minutes'], ['fix', 'hra.r4', 'hra.r4s', 10, 'hra.times']] as const).map(([k, a, b, hi, u]) => (
                  <div key={k} className={s.rule}>
                    <div className={s.rk}><div className={s.krK}>{t(a)}</div><div className={s.sub}>{t(b)}</div></div>
                    <div className={s.row} style={{ gap: 4 }}>
                      <button type="button" className={s.sq} aria-label={t('hra.dec')} onClick={ruleStep(k, -1, hi)}>−</button>
                      <b className={s.stepV}>{t(u, { n: ru[k] })}</b>
                      <button type="button" className={s.sq} aria-label={t('hra.inc')} onClick={ruleStep(k, 1, hi)}>+</button>
                    </div>
                  </div>
                ))}
                <div className={s.rule}>
                  <div className={s.rk}><div className={s.krK}>{t('hra.r5')}</div><div className={s.sub}>{t('hra.r5s')}</div></div>
                  <Segmented kind="toggle" label={t('hra.r5')} value={ru.otR} onChange={(v) => setRu({ ...ru, otR: v })} items={[{ key: '15', label: t('hra.r5a') }, { key: '30', label: t('hra.r5b') }]} />
                </div>
                <div className={s.rule}>
                  <div className={s.rk}><div className={s.krK}>{t('hra.r6')}</div><div className={s.sub}>{t('hra.r6s')}</div></div>
                  <Segmented kind="toggle" label={t('hra.r6')} value={ru.out} onChange={(v) => setRu({ ...ru, out: v })} items={[{ key: 'block', label: t('hra.r6a') }, { key: 'ask', label: t('hra.r6b') }]} />
                </div>
                <div className={s.rule}><div className={s.rk}><div className={s.krK}>{t('hra.r7')}</div><div className={s.sub}>{t('hra.r7s')}</div></div><Switch on={ru.otNeed} label={t('hra.r7')} onClick={() => setRu({ ...ru, otNeed: !ru.otNeed })} /></div>
                <div className={s.rule}><div className={s.rk}><div className={s.krK}>{t('hra.r8')}</div><div className={s.sub}>{t('hra.r8s')}</div></div><Switch on={ru.photo} label={t('hra.r8')} onClick={() => setRu({ ...ru, photo: !ru.photo })} /></div>
              </section>
              <aside className={s.card} aria-label={t('hra.ncrHd')}>
                <h2>{t('hra.ncrHd')}</h2>
                <div className={s.sub}>{t('hra.ncrNote')}</div>
                <div className={s.filters}>
                  {ncr.map((u) => (
                    <span key={u} className={s.tag}>{u}<button type="button" aria-label={t('hra.ncrRm', { u })} onClick={() => { const prev = ncr; setNcr(ncr.filter((x) => x !== u)); say(t('hra.tNcrRm', { u }), () => setNcr(prev)); }}>×</button></span>
                  ))}
                  <button type="button" className={s.act} onClick={() => say(t('hra.tNcrAdd'))}>{t('hra.ncrAdd')}</button>
                </div>
              </aside>
            </div>
          )}
        </StateView>
      </div>

      <Modal
        open={!!cs && !!cell}
        onOpenChange={(o) => !o && closeCell()}
        title={cs && cell ? `${cs.user} · ${DOW[dowOf(cell.d)]} ${cell.d}/Oct/2026` : ''}
        description={cs ? `${cs.name} · ${cs.seat} · ${BRANCH_NAME[cs.branch]}` : undefined}
        footer={cs && cell ? (
          <>
            {(cc === '!' || cc === 'M') && <Button onClick={() => { say(t('hra.tRemind', { u: cs.user })); closeCell(); }}>{t('hra.cdRemind')}</Button>}
            {ed ? <Button variant="primary" onClick={saveCell}>{t('hra.cdSave')}</Button> : <Button variant="primary" onClick={() => setEd({ c: cc, why: '', tried: false })}>{t('hra.cdEdit')}</Button>}
          </>
        ) : undefined}
      >
        {cs && cell && (
          <div className={s.cd}>
            <div><Chip tone={CODE[cc].tone === 'mute' ? 'neutral' : CODE[cc].tone === 'pri' ? 'info' : CODE[cc].tone}>{t(CODE_KEY[cc])}</Chip></div>
            {(() => {
              const [pin, pout] = punchOf(cc, cell.d);
              const meth = POINT_METHODS[cs.branch][cell.d % POINT_METHODS[cs.branch].length];
              const rows: [string, string][] = [
                [t('hra.cdIn'), pin], [t('hra.cdOut'), pout], [t('hra.cdHow'), pin === '—' ? '—' : `${meth} · ${BRANCH_NAME[cs.branch]}`],
                [t('hra.cdShift'), kindOf(cal, cell.d) === 'half' ? t('hra.cdShiftHalf') : t('hra.cdShiftStd')],
                [t('hra.cdReq'), cc === 'P' ? `NP-2610-0${10 + cell.d} · đã duyệt` : cc === 'CT' ? `CT-2610-00${cell.d % 9} · đã duyệt` : cc === '!' ? t('hra.cdNoExp') : '—'],
              ];
              return rows.map(([k, v]) => <div key={k} className={s.cdRow}><span>{k}</span><b>{v}</b></div>);
            })()}
            {ed && (
              <div className={s.nf}>
                <div className={s.nfK}>{t('hra.cdEditTo')}</div>
                <div className={s.filters}>{EDIT_CODES.map(([k]) => <Pill key={k} big on={ed.c === k} onClick={() => setEd({ ...ed, c: k })}>{t(ED_KEY[k]!)}</Pill>)}</div>
                <label>
                  <div className={s.nfK}>{t('hra.cdWhy')} <span className={s.errc}>*</span></div>
                  <input aria-label={t('hra.cdWhyAria')} aria-invalid={ed.tried && !ed.why.trim()} className={s.nfIn} value={ed.why} placeholder={t('hra.cdWhyPh')} onChange={(e) => setEd({ ...ed, why: e.target.value })} />
                </label>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal open={!!qrOf} onOpenChange={(o) => !o && setQrOf(null)} title={t('hra.qrTitle', { p: qrOf ?? '' })}
        footer={<Button onClick={() => { setQrOf(null); say(t('hra.tQrPrint')); }}>{t('hra.qrPrint')}</Button>}>
        <div className={s.qr}>
          <div className={s.qrBox}>
            <svg role="img" aria-label={t('hra.qrImg')} width="168" height="168" viewBox="0 0 168 168">{qrCells().map(([x, y]) => <rect key={`${x}-${y}`} x={x * 8} y={y * 8} width="8" height="8" fill="currentColor" />)}</svg>
          </div>
          <div className={s.sub}>{t('hra.qrNote')}</div>
        </div>
      </Modal>

      <Modal open={!!pf} onOpenChange={(o) => !o && setPf(null)} title={t('hra.pfTitle')}
        footer={<><Button variant="ghost" onClick={() => setPf(null)}>{t('hra.pfCancel')}</Button><Button variant="primary" onClick={submitPf}>{t('hra.pfAdd')}</Button></>}>
        {pf && (
          <div className={s.nf}>
            <label><div className={s.nfK}>{t('hra.pfName')} <span className={s.errc}>*</span></div>
              <input aria-label={t('hra.pfName')} aria-invalid={pf.tried && !pf.name.trim()} className={s.nfIn} value={pf.name} placeholder={t('hra.pfNamePh')} onChange={(e) => setPf({ ...pf, name: e.target.value })} /></label>
            <label><div className={s.nfK}>{t('hra.pfAddr')}</div>
              <input aria-label={t('hra.pfAddr')} className={s.nfIn} value={pf.addr} placeholder={t('hra.pfAddrPh')} onChange={(e) => setPf({ ...pf, addr: e.target.value })} /></label>
            <label><div className={s.nfK}>{t('hra.pfRadius')}</div>
              <input aria-label={t('hra.pfRadius')} inputMode="numeric" className={`${s.nfIn} ${s.nfShort}`} value={pf.r} onChange={(e) => setPf({ ...pf, r: e.target.value })} /></label>
            <div><div className={s.nfK}>{t('hra.pfHow')}</div>
              <div className={s.filters}>{METHODS.map(([k, lb]) => <Pill key={k} big on={!!pf.m[k]} onClick={() => setPf({ ...pf, m: { ...pf.m, [k]: !pf.m[k] } })}>{lb}</Pill>)}</div></div>
          </div>
        )}
      </Modal>

      <Modal open={confirmLock} onOpenChange={setConfirmLock} danger title={t('hra.lockTitle')} description={t('hra.lockText', { n: STAFF.length - xnN })}
        footer={<><Button onClick={() => setConfirmLock(false)}>{t('hra.lockLater')}</Button><Button variant="primary" onClick={() => { setConfirmLock(false); setLocked(true); say(t('hra.tLockedDone')); }}>{t('hra.lockOk')}</Button></>}>
        <span />
      </Modal>
    </Page>
  );
}

/** Nhóm ô cùng hàng của lưới CSS (display: contents) để rowheader + ô vẫn là con trực tiếp của lưới. */
function SheetRow({ children }: { children: React.ReactNode }) {
  return <div role="row" className={s.contents}>{children}</div>;
}
